import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { todayStr } from '../lib/format.js';
import { inputStyle } from '../lib/ui.js';
import { ProfileSection } from './ProfileSection.jsx';

// Work Habits (was "Reliability score", reframed CJ 2026-07-29). No single number
// or grade — a number doesn't help someone feel good or improve. Instead we show,
// in plain language, how things have been going over the last 90 days, category by
// category, expandable to the specifics. The point is to be helpful (the person
// sees where they stand) and to let a manager spot a real problem — not to
// demoralize. Same data source as before (GET /api/reliability/:id); just
// re-presented. Managers keep the log-an-incident + void affordances.

const KIND_LABEL = {
  no_show: 'No-show',
  late_callout: 'Late call-out',
  tardy_10: 'Clocked in 10+ min late',
  tardy_30: 'Clocked in 30+ min late',
  left_early: 'Left early',
  opening_task_incomplete: 'Opening tasks left incomplete',
  closing_task_incomplete: 'Closing tasks left incomplete',
  disputed_task: 'A task completion was disputed',
  tor_ignored: 'Time-off request left unanswered',
  sick: 'Out sick',
  excused: 'Excused absence',
};
const TASK_KINDS = new Set(['opening_task_incomplete', 'closing_task_incomplete', 'disputed_task']);

// One category's plain-language summary + its expandable detail rows.
function HabitCategory({ title, cleanLine, issues }) {
  const [open, setOpen] = useState(false);
  const hasIssues = issues.length > 0;
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #f2f2f2' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, color: hasIssues ? '#8a5a1f' : '#2e7d32', fontWeight: 600 }}>
          <span style={{ marginRight: 6 }}>{hasIssues ? '•' : '✓'}</span>
          {title}
        </div>
        {hasIssues && (
          <button onClick={() => setOpen((o) => !o)}
            style={{ fontSize: 11, color: '#1565c0', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            {open ? 'Hide' : 'Details'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#37474f', marginTop: 3, paddingLeft: 18 }}>
        {hasIssues ? issues.map((i) => i.line).join(' · ') : cleanLine}
      </div>
      {open && hasIssues && (
        <div style={{ marginTop: 6, paddingLeft: 18 }}>
          {issues.flatMap((i) => i.rows).map((r, idx) => (
            <div key={idx} style={{ fontSize: 12, color: '#616161', padding: '2px 0' }}>
              <span style={{ color: '#9e9e9e' }}>{r.date}</span> · {r.label}{r.note ? ` — ${r.note}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReliabilityScoreCard({ currentUser, targetUserId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [voidOpen, setVoidOpen] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({ kind: 'no_show', incident_date: todayStr(), note: '', penalty_points: '' });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const headers = { 'x-user-id': String(currentUser.id) };

  async function load() {
    setError('');
    try {
      const r = await api('GET', `/api/reliability/${targetUserId}`, null, headers);
      setData(r);
    } catch (e) { setError(e.message || 'Failed to load work habits'); }
  }
  useEffect(() => { load(); }, [targetUserId]);

  async function voidIncident(id) {
    if (!voidReason.trim()) { setError('Reason required to void.'); return; }
    try {
      await api('PUT', `/api/reliability/incidents/${id}/void`, { reason: voidReason }, headers);
      setVoidOpen(null); setVoidReason(''); load();
    } catch (e) { setError(e.message || 'Void failed'); }
  }

  async function submitIncident() {
    if (!logForm.incident_date || !logForm.kind) { setError('Date and kind required.'); return; }
    const body = { employee_id: Number(targetUserId), incident_date: logForm.incident_date, kind: logForm.kind, note: logForm.note || null };
    if (logForm.kind === 'other') {
      const pts = Number(logForm.penalty_points);
      if (!Number.isFinite(pts) || pts <= 0) { setError('Penalty points required for custom incident.'); return; }
      body.component = 'attendance'; body.penalty_points = pts;
    } else if (logForm.penalty_points) { body.penalty_points = Number(logForm.penalty_points); }
    try {
      await api('POST', '/api/reliability/incidents', body, headers);
      setLogOpen(false);
      setLogForm({ kind: 'no_show', incident_date: todayStr(), note: '', penalty_points: '' });
      setError(''); load();
    } catch (e) { setError(e.message || 'Log failed'); }
  }

  if (error) return (
    <ProfileSection title="Work Habits" canEdit={false}>
      <div style={{ fontSize: 12, color: '#c62828' }}>{error}</div>
    </ProfileSection>
  );
  if (!data) return (
    <ProfileSection title="Work Habits" canEdit={false}>
      <div style={{ fontSize: 12, color: '#757575' }}>Loading…</div>
    </ProfileSection>
  );

  // Non-voided incidents only — voided ones were reversed and shouldn't color
  // how someone sees their record.
  const live = (data.incidents || []).filter((i) => !i.voided_at);
  const countBy = (kinds) => live.filter((i) => kinds.has(i.kind));
  const rowsFor = (kinds) => countBy(kinds).map((i) => ({
    date: i.incident_date, label: KIND_LABEL[i.kind] || String(i.kind).replace(/_/g, ' '), note: i.note || '',
  }));

  // Attendance summary — count the meaningful kinds and phrase them plainly.
  const lateN = live.filter((i) => i.kind === 'tardy_10' || i.kind === 'tardy_30').length;
  const noShowN = live.filter((i) => i.kind === 'no_show').length;
  const calloutN = live.filter((i) => i.kind === 'late_callout').length;
  const leftEarlyN = live.filter((i) => i.kind === 'left_early').length;
  const attIssues = [];
  if (lateN) attIssues.push({ line: `Clocked in late ${lateN} time${lateN === 1 ? '' : 's'}`, rows: rowsFor(new Set(['tardy_10', 'tardy_30'])) });
  if (noShowN) attIssues.push({ line: `${noShowN} no-show${noShowN === 1 ? '' : 's'}`, rows: rowsFor(new Set(['no_show'])) });
  if (calloutN) attIssues.push({ line: `${calloutN} late call-out${calloutN === 1 ? '' : 's'}`, rows: rowsFor(new Set(['late_callout'])) });
  if (leftEarlyN) attIssues.push({ line: `Left early ${leftEarlyN} time${leftEarlyN === 1 ? '' : 's'}`, rows: rowsFor(new Set(['left_early'])) });

  // Tasks summary — prefer the incident-level detail; fall back to the
  // completion % the component already computes.
  const taskRows = rowsFor(TASK_KINDS);
  const taskC = data.components && data.components.task_completion;
  const taskIssues = [];
  if (taskRows.length) taskIssues.push({ line: `${taskRows.length} task issue${taskRows.length === 1 ? '' : 's'} noted`, rows: taskRows });
  else if (taskC && taskC.completion_pct != null && taskC.completion_pct < 100) {
    taskIssues.push({ line: `${taskC.completion_pct}% of assigned tasks completed`, rows: [] });
  }

  const allClean = attIssues.length === 0 && taskIssues.length === 0;

  return (
    <ProfileSection title="Work Habits" canEdit={false}>
      <div style={{ fontSize: 11, color: '#9e9e9e', marginBottom: 6 }}>How things have been going over the last 90 days.</div>
      {allClean && (
        <div style={{ fontSize: 13, color: '#2e7d32', fontWeight: 600, padding: '6px 0' }}>
          ✓ You’re on track — nothing to flag. Thanks for being dependable.
        </div>
      )}
      <HabitCategory
        title="Attendance & punctuality"
        cleanLine="On time for every shift on record."
        issues={attIssues} />
      <HabitCategory
        title="Tasks"
        cleanLine={taskC && taskC.completion_pct == null ? 'No tasks tracked yet.' : 'All assigned tasks completed.'}
        issues={taskIssues} />

      {/* Full incident history stays available behind a tap (managers + the
          person themselves) so nothing is hidden, just not front-and-center. */}
      {live.length > 0 || (data.incidents && data.incidents.length > 0) ? (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => setDetailsOpen((o) => !o)}
            style={{ fontSize: 11, color: '#1565c0', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {detailsOpen ? 'Hide full history' : 'Show full history'}
          </button>
          {detailsOpen && (
            <table style={{ width: '100%', fontSize: 11, marginTop: 6 }}>
              <tbody>
                {data.incidents.map((inc) => (
                  <tr key={inc.id} style={{ borderBottom: '1px solid #fafafa', opacity: inc.voided_at ? 0.5 : 1 }}>
                    <td style={{ padding: '4px 0', color: '#757575', whiteSpace: 'nowrap' }}>{inc.incident_date}</td>
                    <td style={{ padding: '4px 8px' }}>{KIND_LABEL[inc.kind] || String(inc.kind).replace(/_/g, ' ')}</td>
                    <td style={{ padding: '4px 8px', color: '#9e9e9e' }}>
                      {inc.voided_at ? `voided · ${inc.voided_reason || ''}` : (inc.note || '')}
                    </td>
                    <td style={{ padding: '4px 0', textAlign: 'right' }}>
                      {data.canVoid && !inc.voided_at && (
                        <button onClick={() => { setVoidOpen(inc.id); setVoidReason(''); setError(''); }}
                          style={{ fontSize: 10, padding: '2px 6px', background: 'transparent', color: '#1565c0', border: 'none', cursor: 'pointer' }}>
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {voidOpen && (
        <div style={{ marginTop: 8, padding: 8, background: '#fafbfc', border: '1px solid #d0d4d9', borderRadius: 6 }}>
          <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Reason for void (logged in audit)" style={inputStyle} />
          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
            <button onClick={() => voidIncident(voidOpen)}
              style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              Void incident
            </button>
            <button onClick={() => setVoidOpen(null)} className="btn-secondary" style={{ fontSize: 11 }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Manager-only: log an observed incident. Same backend as before. */}
      {data.canVoid && (
        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
          {!logOpen ? (
            <button onClick={() => setLogOpen(true)}
              style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #d0d4d9', background: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              + Log an incident
            </button>
          ) : (
            <div style={{ padding: 10, background: '#fafbfc', border: '1px solid #d0d4d9', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', marginBottom: 6 }}>
                Log a manager-observed incident. The employee sees this in their history.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
                <select value={logForm.kind} onChange={(e) => setLogForm(f => ({ ...f, kind: e.target.value, penalty_points: '' }))}
                  style={{ ...inputStyle, fontSize: 12 }}>
                  <option value="sick">Sick / illness (0, tracked)</option>
                  <option value="excused">Excused absence (0, tracked)</option>
                  <option value="no_show">No-show (−12)</option>
                  <option value="late_callout">Late call-out (−6)</option>
                  <option value="tardy_30">Tardy 30+ min (−4)</option>
                  <option value="tardy_10">Tardy 10+ min (−2)</option>
                  <option value="disputed_task">Disputed task (−5)</option>
                  <option value="other">Other (custom penalty)</option>
                </select>
                <input type="date" value={logForm.incident_date}
                  onChange={(e) => setLogForm(f => ({ ...f, incident_date: e.target.value }))}
                  style={{ ...inputStyle, fontSize: 12 }} />
                {logForm.kind === 'other' && (
                  <input type="number" min="1" max="20" placeholder="Penalty pts" value={logForm.penalty_points}
                    onChange={(e) => setLogForm(f => ({ ...f, penalty_points: e.target.value }))}
                    style={{ ...inputStyle, fontSize: 12 }} />
                )}
              </div>
              <input value={logForm.note} onChange={(e) => setLogForm(f => ({ ...f, note: e.target.value }))}
                placeholder="Reason (visible to the employee)" style={{ ...inputStyle, fontSize: 12, marginTop: 6 }} />
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button onClick={submitIncident}
                  style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  Log incident
                </button>
                <button onClick={() => { setLogOpen(false); setError(''); }} className="btn-secondary" style={{ fontSize: 11 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </ProfileSection>
  );
}
